<template>
  <div v-if="modelValue" class="dialog-overlay" @click.self="handleClose" @keydown.esc="handleClose" tabindex="-1" ref="modalOverlay">
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 class="dialog-title">{{ title }}</h2>
        <button class="dialog-close" @click="handleClose" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>
      <div class="dialog-content">
        <p class="dialog-message">{{ message }}</p>
        <div class="dialog-benefits">
          <div class="benefit-item">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.667 5L7.5 14.167L3.333 10" stroke="#12BF57" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <span>Access detailed cashflow schedules</span>
          </div>
          <div class="benefit-item">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.667 5L7.5 14.167L3.333 10" stroke="#12BF57" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <span>Download diligence documents</span>
          </div>
          <div class="benefit-item">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.667 5L7.5 14.167L3.333 10" stroke="#12BF57" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <span>Invest securely in bonds</span>
          </div>
        </div>
      </div>
      <div class="dialog-actions">
        <button aria-label="Signup now to start investing" class="dialog-button dialog-button--primary"
          @click="handleSignupClick">
          Signup Now
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
defineOptions({ name: 'BondSignupGateModal' })

const props = defineProps<{
  modelValue: boolean
  title?: string
  message?: string
  signupUrl?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const handleClose = () => {
  emit('update:modelValue', false)
}

const handleSignupClick = () => {
  if (props.signupUrl) {
    window.open(props.signupUrl, '_blank')
  }
}

if (typeof window !== "undefined") {
  window.addEventListener('close-all-overlays', () => {
    handleClose();
  });
}

const modalOverlay = ref<HTMLElement | null>(null);
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    nextTick(() => {
      modalOverlay.value?.focus();
    });
  }
});
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.dialog-container {
  background: #ffffff;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #EAF7FF;
}

.dialog-title {
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 700;
  color: #1b2559;
  margin: 0;
  line-height: 28px;
}

.dialog-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #A3AED0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  border-radius: 4px;
}

.dialog-close:hover {
  color: #1b2559;
  background: #F9FBFF;
}

.dialog-content {
  padding: 24px;
}

.dialog-message {
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 400;
  color: #1b2559;
  line-height: 24px;
  margin: 0 0 20px;
}

.dialog-benefits {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  padding: 16px;
  background: #F9FBFF;
  border-radius: 8px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  color: #1b2559;
  line-height: 20px;
}

.dialog-actions {
  padding: 16px 24px 24px;
  border-top: 1px solid #EAF7FF;
  justify-content: center;
}

.dialog-button {
  width: 100%;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #0027AF;
  color: #ffffff;
}

.dialog-button:hover {
  background: #001f8a;
}
</style>
